import { CompanyLocationService } from '../../api/services/CompanyLocationService';
import { CompanySettingsService } from '../../api/services/CompanySettingsService';
import { Routemize_Constants_AddressType } from '../../api/models/Routemize_Constants_AddressType';
import { Routemize_Constants_BaseLocation } from '../../api/models/Routemize_Constants_BaseLocation';
import type { Routemize_DTOs_CompanyLocationDto } from '../../api/models/Routemize_DTOs_CompanyLocationDto';
import type { Routemize_DTOs_CreateUpdateCompanyLocationDto } from '../../api/models/Routemize_DTOs_CreateUpdateCompanyLocationDto';
import type { Routemize_DTOs_CompanySettingsDTO } from '../../api/models/Routemize_DTOs_CompanySettingsDTO';
import type { Routemize_DTOs_CreateUpdateCompanySettingsDTO } from '../../api/models/Routemize_DTOs_CreateUpdateCompanySettingsDTO';
import type { Routemize_ValueObjects_RoutePreferences } from '../../api/models/Routemize_ValueObjects_RoutePreferences';
import { addressManager, type UserAddress } from '../users/AddressManager';
import { resolveHomeUserAddress } from '../../utils/userAddressUtils';

export type BaseLocationChoice = 'home' | 'office';
export type EndLocationChoice = BaseLocationChoice | 'same';

export type FirstAppointmentScheduling = 'starts_at_work_hours' | 'includes_travel_time';
export type LastAppointmentScheduling = 'ends_at_work_hours' | 'includes_return_travel';

/**
 * The address fields the wizard collects.
 *
 * `zipCode` is deliberately the only postal field. `Routemize_DTOs_UserAddressDto`
 * and `Routemize_DTOs_CreateUpdateCompanyLocationDto` both expose `zipCode` and
 * neither has a `postalCode`, so anything written to `postalCode` is dropped on
 * serialization. `AddressData` from Google Places also returns `zipCode`.
 */
export interface AddressFieldValues {
  addressLine1: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

export const emptyAddressFields = (country: string): AddressFieldValues => ({
  addressLine1: '',
  city: '',
  state: '',
  zipCode: '',
  country,
});

/**
 * Both `UserAddress` and `Routemize_DTOs_CompanyLocationDto` expose the same
 * address field names, so one mapper covers the home address and the office.
 */
export const addressFieldsFrom = (
  source:
    | {
        addressLine1?: string | null;
        city?: string | null;
        state?: string | null;
        zipCode?: string | null;
        country?: string | null;
      }
    | null
    | undefined,
  fallbackCountry: string
): AddressFieldValues => ({
  addressLine1: source?.addressLine1 ?? '',
  city: source?.city ?? '',
  state: source?.state ?? '',
  zipCode: source?.zipCode ?? '',
  country: source?.country ?? fallbackCountry,
});

export const isAddressComplete = (fields: AddressFieldValues): boolean =>
  Boolean(
    fields.addressLine1.trim() &&
      fields.city.trim() &&
      fields.state.trim() &&
      fields.zipCode.trim()
  );

export const formatAddressLine = (
  parts: { city?: string | null; state?: string | null; zipCode?: string | null }
): string =>
  [parts.city, parts.state].filter(Boolean).join(', ') +
  (parts.zipCode ? ` ${parts.zipCode}` : '');

export interface WorkAreaContext {
  homeAddress: UserAddress | null;
  officeLocation: Routemize_DTOs_CompanyLocationDto | null;
  settings: Routemize_DTOs_CompanySettingsDTO | null;
}

export async function loadWorkAreaContext(
  userId: string | null | undefined
): Promise<WorkAreaContext> {
  const [addresses, locations, settings] = await Promise.all([
    userId ? addressManager.getUserAddresses(userId) : Promise.resolve([]),
    CompanyLocationService.getApiAppCompanyLocationGetAllActive(),
    CompanySettingsService.getApiAppCompanySettingsGet(),
  ]);

  return {
    homeAddress: resolveHomeUserAddress(addresses ?? []) ?? null,
    officeLocation: locations?.find(l => l.isDefault) ?? locations?.[0] ?? null,
    settings: settings ?? null,
  };
}

export async function saveHomeAddress(
  userId: string,
  fields: AddressFieldValues,
  existing: UserAddress | null
): Promise<UserAddress | null> {
  const address: UserAddress = {
    addressType: Routemize_Constants_AddressType.HOME,
    addressLine1: fields.addressLine1,
    city: fields.city,
    state: fields.state,
    zipCode: fields.zipCode,
    country: fields.country,
    isActive: true,
  };

  if (existing?.id) {
    await addressManager.updateUserAddress(userId, existing.id, { ...existing, ...address });
  } else {
    await addressManager.addUserAddress(userId, address);
  }

  const addresses = await addressManager.getUserAddresses(userId);
  return resolveHomeUserAddress(addresses ?? []) ?? null;
}

export async function saveOfficeLocation(
  locationName: string,
  fields: AddressFieldValues,
  existing: Routemize_DTOs_CompanyLocationDto | null
): Promise<Routemize_DTOs_CompanyLocationDto | null> {
  // Latitude/longitude are not on the create/update DTO — the backend geocodes
  // from the address and returns coordinates on the read model.
  const location: Routemize_DTOs_CreateUpdateCompanyLocationDto = {
    locationName: locationName.trim() || 'Main Office',
    addressLine1: fields.addressLine1,
    city: fields.city,
    state: fields.state,
    zipCode: fields.zipCode,
    country: fields.country,
    locationType: Routemize_Constants_BaseLocation.MAIN_OFFICE,
    isActive: true,
    isDefault: true,
  };

  if (existing?.id) {
    await CompanyLocationService.putApiAppCompanyLocationUpdate(existing.id, location);
  } else {
    await CompanyLocationService.postApiAppCompanyLocationCreate(location);
  }

  const locations = await CompanyLocationService.getApiAppCompanyLocationGetAllActive();
  return locations?.find(l => l.isDefault) ?? locations?.[0] ?? null;
}

/**
 * Read-modify-write against company settings.
 *
 * Only the changed top-level fields are sent: the backend preserves any
 * top-level field we omit, so a stale snapshot can't clobber unrelated
 * settings. `routePreferences` is the exception — it is a nested value object
 * the backend replaces wholesale, so existing keys must be spread back in.
 */
async function patchCompanySettings(
  build: (current: Routemize_DTOs_CompanySettingsDTO) => Routemize_DTOs_CreateUpdateCompanySettingsDTO
): Promise<void> {
  const current = await CompanySettingsService.getApiAppCompanySettingsGet();
  if (!current.id) {
    throw new Error('Company settings are not available yet. Please refresh and try again.');
  }
  await CompanySettingsService.putApiAppCompanySettingsUpdate(current.id, build(current));
}

export function resolveEndLocation(
  end: EndLocationChoice,
  start: BaseLocationChoice
): BaseLocationChoice {
  return end === 'same' ? start : end;
}

export async function saveBaseLocations(input: {
  startLocation: BaseLocationChoice;
  endLocation: EndLocationChoice;
}): Promise<void> {
  await patchCompanySettings(current => {
    const routePreferences: Routemize_ValueObjects_RoutePreferences = {
      ...(current.routePreferences ?? {}),
      defaultStartingLocation: input.startLocation,
      defaultEndingLocation: resolveEndLocation(input.endLocation, input.startLocation),
    };
    return { routePreferences };
  });
}

export async function saveServiceRadius(radiusMiles: number): Promise<void> {
  await patchCompanySettings(() => ({ maxTravelRadiusMiles: radiusMiles }));
}

export async function saveTravelTimeRules(input: {
  firstAppointmentScheduling: FirstAppointmentScheduling;
  lastAppointmentScheduling: LastAppointmentScheduling;
}): Promise<void> {
  await patchCompanySettings(current => {
    const routePreferences: Routemize_ValueObjects_RoutePreferences = {
      ...(current.routePreferences ?? {}),
      firstAppointmentScheduling: input.firstAppointmentScheduling,
      lastAppointmentScheduling: input.lastAppointmentScheduling,
    };
    return { routePreferences };
  });
}
